import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, Check } from 'lucide-react'
import './Auth.css'
import FreeTrialModal from '../../components/FreeTrialModal'
import SEO from '../../components/SEO'

export default function Signup() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showTrialModal, setShowTrialModal] = useState(false)
  
  const { signup, loginWithGoogle } = useAuth()
  const navigate = useNavigate()

  /* ... passwordRequirements ... */
  const passwordRequirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Contains a number', met: /\d/.test(password) },
    { label: 'Contains a letter', met: /[a-zA-Z]/.test(password) }
  ]

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      return setError('Passwords do not match')
    }

    if (!passwordRequirements.every(req => req.met)) {
      return setError('Please meet all password requirements')
    }

    setLoading(true)

    try {
      await signup(email, password, name)
      // Show trial modal instead of navigating immediately
      setShowTrialModal(true)
    } catch (err) {
      setError(getErrorMessage(err.code))
      setLoading(false)
    } 
    // Note: setLoading(false) is handled in catch or stayed true while modal is showing to prevent double submit? 
    // Actually we should probably unset loading if we show modal, but the modal is an overlay. 
    // Let's set loading false so form is interactive if they close modal (though modal navigates away usually).
    // Better: keep loading false here.
    setLoading(false)
  }

  async function handleGoogleSignup() {
    setError('')
    setLoading(true)

    try {
      const { isNewUser } = await loginWithGoogle()
      if (isNewUser) {
        setShowTrialModal(true)
      } else {
        navigate('/dashboard')
      }
    } catch (err) {
      setError(getErrorMessage(err.code))
    } finally {
      setLoading(false)
    }
  }

  /* ... getErrorMessage ... */
  function getErrorMessage(code) {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists'
      case 'auth/invalid-email':
        return 'Invalid email address'
      case 'auth/weak-password':
        return 'Password is too weak'
      default:
        return 'Failed to create account. Please try again.'
    }
  }

  return (
    <div className="auth-page">
      <SEO 
        title="Sign Up | Create Your Digital vCard"
        description="Join Digital vCard today. Create your professional digital profile in minutes. No credit card required for trial."
      />
      <FreeTrialModal isOpen={showTrialModal} onClose={() => navigate('/dashboard')} />
      
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <img src="/assets/logo/logo.png" alt="Digital vCard" className="logo-image" style={{ height: '40px' }} />
            <span>Digital vCard</span>
          </Link>
          <h1>Create Account</h1>
          <p>Get started with your digital vCard today</p>
        </div>

        {error && (
          <div className="alert alert-error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <div className="input-icon-wrapper">
              <User size={20} className="input-icon" />
              <input
                type="text"
                className="form-input"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <div className="input-icon-wrapper">
              <Mail size={20} className="input-icon" />
              <input
                type="email"
                className="form-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="input-icon-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            {password && (
              <div className="password-requirements">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className={`requirement ${req.met ? 'met' : ''}`}>
                    <Check size={14} />
                    <span>{req.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <div className="input-icon-wrapper">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? <span className="spinner"></span> : 'Create Account'}
          </button>
        </form>

        <div className="auth-divider">
          <span>or continue with</span>
        </div>

        <button 
          onClick={handleGoogleSignup}
          className="btn btn-secondary google-btn"
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="auth-switch">
          Already have an account? <Link to="/login" className="link">Sign in</Link>
        </p>

        <p className="auth-terms">
          By creating an account, you agree to our{' '}
          <a href="#" className="link">Terms of Service</a> and{' '}
          <a href="#" className="link">Privacy Policy</a>
        </p>
      </div>
    </div>
  )
}
